/**
 * Every string the Create Multiple Users wizard shows (PD-4066), taken from the
 * Figma flow (file 1emHQYcS80Ax4DRFgB3Lak, node 787:76930).
 *
 * Kept in one place for the same reasons as the single-user wizard's copy: it is
 * the part most likely to be revised, and several strings are asserted in tests.
 *
 * The mockup's step tracker is inconsistent between frames — two summary frames
 * label step 1 "Team & Account & Details" while every other frame labels it
 * "Upload". "Upload" is used here: it is what the step actually contains, and
 * Portal has no Teams (PD-4213).
 */

export const WIZARD_TITLE = 'Create Multiple Users';

export const REQUIRED_FIELDS = 'Required Fields';

export const STEPS = {
  upload: 'Upload',
  summary: 'Summary',
};

export const UPLOAD_STEP = {
  title: 'List of Users',
  fieldLabel: 'Upload File',
  help:
    'Choose a CSV file to create multiple users. The file must include unique email and mobile phone number values for each user.',
  upload: 'UPLOAD CSV FILE',
  uploading: 'PROCESSING...',
  template: 'DOWNLOAD CSV TEMPLATE',
  // The design states both limits on one line. They are the server's limits too
  // — see BulkImportable and BulkUserImport::MAX_ROWS.
  limits:
    'Max file size 10 MB. and/or 40,000 Users. One User per row required.',
  remove: 'Remove file',
};

/**
 * What the upload step says when a file is rejected.
 *
 * The size and type checks run in the browser as well as on the server, because
 * the design shows them against the field the moment a file is chosen and a
 * 10 MB round trip to be told "too big" is exactly what that avoids. The server
 * repeats both — see BulkImportable — since the browser's answer is not a
 * guarantee of anything.
 */
export const UPLOAD_ERRORS = {
  tooLarge: 'File exceeds the maximum size of 10 MB',
  wrongType: 'Invalid file type. Please upload a csv file.',
};

/**
 * One deliberate divergence from the design, worth knowing about.
 *
 * Frame 1.2.2 shows "Invalid user details found in the uploaded file." against
 * the upload field, before the wizard advances. That is only possible if the
 * rows are validated in the request — and the whole point of this ticket is
 * that they are not: a 40,000-row file is processed in a worker, and the wizard
 * moves on as soon as the upload is accepted.
 *
 * So a file that parses but yields nothing usable ends as a failed import and
 * is reported on the Summary step (SUMMARY_PANEL.empty) with the per-row
 * reasons in the downloadable report, rather than as a field error here. The
 * two checks that can be made without reading the rows — size and type — do
 * still appear against the field, as the design has them.
 */

export const PROCESSING_PANEL = {
  title: 'Creating Users...',
  body:
    "Processing user creation. Closing this window won't interrupt it. Applying teams, accounts, and permissions.",
  action: 'CREATING USERS...',
  close: 'CLOSE',
};

export const SUMMARY_PANEL = {
  title: 'Summary',
  download: 'DOWNLOAD REPORT',
  close: 'CLOSE',
  success: (users, accounts) =>
    `${users} Users have been created and are ready to use for ${accounts} Accounts.`,
  partialSucceeded: (users) => `${users} Users successfully created.`,
  partialFailed: (users) => `${users} Users failed to be created.`,
  failure: (users) =>
    `All ${users} Users failed to be created to the Accounts.`,
  // Nothing was processed at all — a file the server could not read as a CSV.
  // The mockup has no frame for it, so it borrows the failure panel's shape.
  empty: 'No users could be read from the uploaded file.',
};

/** The three toasts, for when the admin closed the wizard (Figma 787:77019). */
export const TOASTS = {
  success: {
    title: 'Users Successfully Created.',
    body: (users) => `${users} Users created to selected Accounts.`,
  },
  partial: {
    title: 'Users Partially Created.',
    body: (succeeded, failed) =>
      `${succeeded} Users successfully created, ${failed} Failed to be created to the selected Accounts.`,
  },
  failure: {
    title: 'Users Upload Failed.',
    body: 'Try again, or contact support if the issue persists.',
  },
  download: 'DOWNLOAD REPORT',
};
